import { Checkbox, ChoiceList } from '@shopify/polaris';
import { useWidgetStore, type WidgetConfig } from '../../../store/widget.store';
import { useShallow } from 'zustand/react/shallow';
import { PlacementComponent } from '../placement.component';
import { pagesToSelect } from '../../../constants/lists';
import { useMemo } from 'react';
import { TabSection } from '../../tab-section';
import { WidgetType } from '../../../widgets/widgets';

type Props = {
    widgetType: WidgetType;
};

export function PlacementTab({ widgetType }: Readonly<Props>) {
    const { placements, setPlacements } = useWidgetStore(
        useShallow(state => ({
            placements: state.placements,
            setPlacements: state.setPlacements,
        }))
    );

    const handlePlacementChange = (value: WidgetConfig['placements'][number]['page'][]) => {
        if (value.length > 0) {
            const existing = placements.map(p => p.page);
            const toAdd = value.filter(v => !existing.includes(v));
            const toDelete = new Set(existing.filter(v => !value.includes(v)));

            const newPlacements = placements
                .filter(p => !toDelete.has(p.page))
                .concat(toAdd.map(page => ({ page, position: 'top' as const, hide_on_hover: false })));

            setPlacements(newPlacements);
        }
    };

    const handleSetPlacement = (
        page: WidgetConfig['placements'][number]['page'],
        placement: {
            position: WidgetConfig['placements'][number]['position'];
        }
    ) => {
        const newPlacements = placements.map(p => (p.page === page ? { ...p, position: placement.position } : p));
        setPlacements(newPlacements);
    };

    const placementPages = useMemo(
        () =>
            pagesToSelect.map(v => ({
                value: v.value,
                label: v.label,
                disabled: widgetType === WidgetType.INVENTORY && v.value !== 'product',
            })),
        [pagesToSelect, widgetType]
    );

    return (
        <div>
            <TabSection header="Widget Placement">
                <ChoiceList
                    title=""
                    choices={placementPages}
                    selected={placements.map(p => p.page)}
                    allowMultiple={true}
                    onChange={handlePlacementChange}
                />
            </TabSection>
            {placements.map(placement => (
                <TabSection
                    key={placement.page}
                    header={`Placement for ${placement.page.charAt(0).toUpperCase() + placement.page.slice(1)} Page`}
                >
                    <PlacementComponent
                        key={`${placement.page}-${placement.position}`}
                        page={placement.page}
                        position={placement.position}
                        onChange={handleSetPlacement}
                        disabled={widgetType === WidgetType.INVENTORY && placement.page !== 'product'}
                    />
                    <Checkbox
                        label={<span style={{ fontStyle: 'italic' }}>Hide on hover for 30 seconds</span>}
                        checked={placement.hide_on_hover}
                        onChange={checked => {
                            const newPlacements = placements.map(p =>
                                p.page === placement.page ? { ...p, hide_on_hover: checked } : p
                            );
                            setPlacements(newPlacements);
                        }}
                        disabled={
                            (placement.page === 'product' && placement.position === 'add_to_cart') ||
                            (widgetType === WidgetType.INVENTORY && placement.page !== 'product')
                        }
                    />
                </TabSection>
            ))}
        </div>
    );
}
